import fecha from 'fecha';
import { isEmpty } from '../../../utils/isEmpty';
import { CANONICAL_URL, LTS_FAQ } from '../constants/SEOConstants';
import { FaqJsonLd, LtsSsrData, TrainDetail } from '../types/LTSTypes';
import { ASYNC_STORAGE_KEYS, LTS_ROUTES_PATH, RECENT_SEARCH_CARDS_LIMIT } from '../constants/LTSConstants';
import { setDataInLocalStorage } from './LocalStorage';
import { isMobile, navigateTo } from '../../../utils/commonUtils';

export const getLTSDateListWithDaysOfRun = (daysOfRun: (boolean | undefined)[] = []): any[] => {
  const dateList: any = [];
  const date = new Date();

  date.setDate(date.getDate() - 4);
  date.setHours(0, 0, 0, 0);

  for (let i = 0; i < 7; i += 1) {
    const newDate = new Date(date.getTime());
    newDate.setDate(date.getDate() + i);
    const dateDetails = {
      ...getDateDetails(newDate.getTime()),
      valid: daysOfRun?.[newDate.getDay()] ?? true
    };
    dateList.push(dateDetails);
  }

  return dateList;
};

export const getDateDetails = (date, addDays = 0) => {
  const days = [
    { value: 'Day Before Yesterday', short: 'Day Before' },
    { value: 'Yesterday', short: 'Yesterday' },
    { value: 'Today', short: 'Today' },
    { value: 'Tomorrow', short: 'Tomorrow' },
    { value: 'Day After Tomorrow', short: 'Day After' }
  ];
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  newDate.setDate(newDate.getDate() + addDays);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayDifference = (newDate.getTime() - today.getTime()) / (24 * 60 * 60 * 1000);
  const day = days?.[dayDifference + 2];
  const dateDetails = {
    dateFormatted: fecha.format(newDate, 'dddd, DD MMMM YY'),
    dateFormattedMini: fecha.format(newDate, 'ddd, DD MMM'),
    dateNumeric: fecha.format(newDate, 'DD-MM-YYYY'),
    dateFormattedSmall: fecha.format(newDate, 'DD MMM YY'),
    date: newDate.getTime(),
    dateTitle: !isEmpty(day) ? day.value : fecha.format(newDate, 'dddd'),
    dateTitleShort: !isEmpty(day) ? day.short : fecha.format(newDate, 'ddd'),
    dateDay: (!isEmpty(day) ? day.short + ', ' : '') + fecha.format(newDate, 'dddd'),
    dayDifference: dayDifference
  };

  return dateDetails;
};

// eslint-disable-next-line
export const debounce = (fn: Function, delay = 300) => {
  let timeoutId: ReturnType<typeof setTimeout>;

  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
};

export const generateFaqJsonLd = () => {
  const { listItems } = LTS_FAQ;

  const jsonLd: FaqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: []
  };

  listItems.forEach((item) => {
    jsonLd.mainEntity.push({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    });
  });

  return jsonLd;
};

export const getLtsSsrData = (path: string): LtsSsrData => {
  const ssrData = { canonicalUrl: '', cacheKey: '', pushToCache: false };

  switch (path) {
    case LTS_ROUTES_PATH.landingPage:
      ssrData.canonicalUrl = CANONICAL_URL.landingPage;
      ssrData.cacheKey = 'HTML_STATIC_LTS-LANDING';
      ssrData.pushToCache = true;
      break;

    case LTS_ROUTES_PATH.trainNumberPage:
      ssrData.canonicalUrl = CANONICAL_URL.trainNumberPage;
      ssrData.cacheKey = 'HTML_STATIC_LTS-TRAIN-NUMBER';
      ssrData.pushToCache = false;
      break;

    case LTS_ROUTES_PATH.datePickerPage:
      ssrData.canonicalUrl = CANONICAL_URL.datePickerPage;
      ssrData.cacheKey = 'HTML_STATIC_LTS-DATE-PICKER';
      ssrData.pushToCache = false;
      break;

    case LTS_ROUTES_PATH.stationPickerPage:
      ssrData.canonicalUrl = CANONICAL_URL.stationPickerPage;
      ssrData.cacheKey = 'HTML_STATIC_LTS-STATION-PICKER';
      ssrData.pushToCache = false;
      break;

    default:
      ssrData.canonicalUrl = CANONICAL_URL.landingPage;
      ssrData.cacheKey = 'HTML_STATIC_LTS-LANDING';
      ssrData.pushToCache = true;
  }

  return ssrData;
};

export const getNewTrainSearchHistory = (
  trainDetail: TrainDetail,
  recentSearchList: TrainDetail[]
): TrainDetail[] | [] => {
  if (!recentSearchList && trainDetail) {
    return [trainDetail];
  }
  if (!trainDetail) {
    return [];
  }
  const similarSearch = recentSearchList.find((searchDetail) => searchDetail.trainNumber === trainDetail.trainNumber);
  if (similarSearch) {
    recentSearchList.splice(recentSearchList.indexOf(similarSearch), 1);
  }
  let updatedRecentSearchList = [trainDetail, ...recentSearchList];
  if (updatedRecentSearchList.length > RECENT_SEARCH_CARDS_LIMIT) {
    updatedRecentSearchList = updatedRecentSearchList.slice(0, RECENT_SEARCH_CARDS_LIMIT);
  }
  return updatedRecentSearchList;
};

export async function saveTrainRecentSearch(recentSearchList: TrainDetail[]) {
  setDataInLocalStorage(ASYNC_STORAGE_KEYS.trainRecentSearchHistory, recentSearchList);
}

export const redirectToLiveTrainPage = (requestDetails: { trainDetail: TrainDetail | undefined }) => {
  const encodedRequestDetails = encodeURIComponent(JSON.stringify(requestDetails));
  const isMobileDevice = isMobile();
  const formattedDate = fecha.format(new Date(), 'YYYY-MM-DD');
  const trainNumber = requestDetails?.trainDetail?.trainNumber;

  if (isMobileDevice) {
    window.open(`https://www.makemytrip.com/railways/live-trains?requestDetails=${encodedRequestDetails}`, '_self');
  } else {
    const url = `https://www.makemytrip.com/railways/railStatus/?q1=${trainNumber}&q3=${formattedDate}`;
    navigateTo(url);
  }
};
